import { useCallback, useEffect, useMemo, useState } from "react";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import FilterablePaginatedTable from "common/Components/FilterablePaginatedTable";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ErrorText, MultiSelectFormControl } from "styles/formStyleComponents";
import PropTypes from "prop-types";
import {
  PLATFORM_USER_ACCESS_LAYOUT,
  PLATFORM_USER_ACCESS_UI,
} from "pages/PlatformUserAccess/PlatformUserAccess.constants";
import { AccessStatusChip } from "common/Components/GenericForm/FieldTypes/AccessStatusSelect";
import {
  ToolbarClearFiltersButton,
  ToolbarInviteButton,
} from "pages/PlatformUserAccess/PlatformUserAccess.styles";
import {
  CUSTOMER_USERS_ACCESS_STATUS_FILTER_OPTIONS,
  DEFAULT_CUSTOMER_USERS_LIST_FILTERS,
  buildRowRoleDropdownOptions,
  deriveCustomerUserAccessVariant,
  formatRoleSelectionDisplay,
  getCustomerUserListRowAction,
  getLockedRolesForSelection,
  isCustomerUserRolesEditable,
  mapCustomerUsersListFiltersToColumnFilters,
  mergeRolesWithLockedRoles,
  validateCustomerUserRolesSelection,
} from "../../Users.constants";
import { AccessActionButton } from "./UsersList.styles";


const roleStringsFromUser = (user) =>
  (Array.isArray(user?.roles) ? user.roles : []).map((r) => String(r));

const displayCell = (value) => {
  const text = String(value ?? "").trim();
  return text || "—";
};

const rolesSelectionEqual = (left, right) => {
  const a = [...(left ?? [])].map(String).sort();
  const b = [...(right ?? [])].map(String).sort();
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

const hasActiveCustomerFilters = (filters) =>
  Object.keys(DEFAULT_CUSTOMER_USERS_LIST_FILTERS).some(
    (key) => (filters?.[key] ?? "") !== ""
  );

const UsersList = ({
  customerId,
  users,
  loading = false,
  pagination = {},
  listFilters,
  onInviteUser,
  onClearFilters,
  onListFiltersChange,
  fetchUsers,
  roleOptions,
  roleLabelMap = {},
  onAssignRoles,
  onPatchCustomerAccess,
  onRowAction,
}) => {
  const { customerUsersTableHeaders: headers } = INFRAMARK_CONSTANTS.users;
  const usersList = Array.isArray(users) ? users : [];
  const grantableOptions = Array.isArray(roleOptions) ? roleOptions : [];
  const {
    page = 0,
    size = 20,
    totalCount = 0,
  } = pagination;

  const [pendingRolesByRowId, setPendingRolesByRowId] = useState({});
  const [roleErrorsByRowId, setRoleErrorsByRowId] = useState({});

  useEffect(() => {
    setPendingRolesByRowId({});
    setRoleErrorsByRowId({});
  }, [usersList, customerId]);

  const getRowRolesValue = useCallback(
    (row) => {
      const rowKey = String(row.id);
      if (pendingRolesByRowId[rowKey] !== undefined) {
        return pendingRolesByRowId[rowKey];
      }
      return roleStringsFromUser(row.sourceUser);
    },
    [pendingRolesByRowId]
  );

  const commitRolesOnClose = useCallback(
    async (row) => {
      if (!isCustomerUserRolesEditable(row.sourceUser)) {
        return;
      }
      const rowKey = String(row.id);
      const pending = pendingRolesByRowId[rowKey];
      if (pending === undefined) {
        return;
      }
      const baseline = roleStringsFromUser(row.sourceUser);
      const rolesError = validateCustomerUserRolesSelection(pending);
      if (rolesError) {
        setRoleErrorsByRowId((previous) => ({
          ...previous,
          [rowKey]: rolesError,
        }));
        setPendingRolesByRowId((previous) => {
          const next = { ...previous };
          delete next[rowKey];
          return next;
        });
        return;
      }
      setRoleErrorsByRowId((previous) => {
        if (!previous[rowKey]) {
          return previous;
        }
        const next = { ...previous };
        delete next[rowKey];
        return next;
      });
      setPendingRolesByRowId((previous) => {
        const next = { ...previous };
        delete next[rowKey];
        return next;
      });
      if (rolesSelectionEqual(pending, baseline)) {
        return;
      }
      await onAssignRoles(row.sourceUser, pending);
    },
    [onAssignRoles, pendingRolesByRowId]
  );

  const rows = useMemo(
    () =>
      usersList.map((user) => ({
        id: user.id ?? user.userId ?? user.username,
        email: user.email ?? "",
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        phone: user.phoneNumber ?? user.phone ?? "",
        accessVariant: deriveCustomerUserAccessVariant(user),
        sourceUser: user,
        accountStatus: user.accountStatus ?? "",
      })),
    [usersList]
  );

  const columns = useMemo(
    () => [
      {
        id: "email",
        label: headers[0],
        sortable: false,
        filter: { type: "text" },
        render: (value, row) => {
          const label = displayCell(value);
          return (
            <Typography component="span" variant="body2">
              {label}
            </Typography>
          );
        },
      },
      {
        id: "firstName",
        label: headers[1],
        sortable: false,
        filter: { type: "text" },
        render: (value) => displayCell(value),
      },
      {
        id: "lastName",
        label: headers[2],
        sortable: false,
        filter: { type: "text" },
        render: (value) => displayCell(value),
      },
      {
        id: "phone",
        label: headers[3],
        sortable: false,
        filter: { type: "text" },
        render: (value) => displayCell(value),
      },
      {
        id: "role",
        label: headers[4],
        sortable: false,
        filter: {
          type: "select",
          options: grantableOptions,
        },
        render: (_, row) => {
          const rowKey = String(row.id);
          //when plan to have more than 25 records, we should not provide inline editing
          const baselineRoles = roleStringsFromUser(row.sourceUser);
          const lockedRoles = getLockedRolesForSelection(
            baselineRoles,
            grantableOptions
          );
          const dropdownOptions = buildRowRoleDropdownOptions(
            grantableOptions,
            lockedRoles,
            roleLabelMap
          );
          const selectedNames = getRowRolesValue(row);
          const rolesEditable = isCustomerUserRolesEditable(row.sourceUser);
          const roleError = roleErrorsByRowId[rowKey];
          const hasRoleChoices =
            grantableOptions.length > 0 || lockedRoles.length > 0;
          return (
            <Box onClick={(e) => e.stopPropagation()} sx={{ minWidth: 200 }}>
              <MultiSelectFormControl
                fullWidth
                size="small"
                error={!!roleError}
                compactInline={false}
                hideLegend={false}
              >
                <InputLabel id={`roles-label-${row.id}`}>Roles</InputLabel>
                <Select
                  labelId={`roles-label-${row.id}`}
                  multiple
                  label="Roles"
                  error={!!roleError}
                  value={selectedNames}
                  onChange={(event) => {
                    if (!rolesEditable) {
                      return;
                    }
                    const raw = event.target.value;
                    const next = Array.isArray(raw)
                      ? raw
                      : typeof raw === "string"
                        ? raw.split(",").filter(Boolean)
                        : [];
                    const merged = mergeRolesWithLockedRoles(next, lockedRoles);
                    setPendingRolesByRowId((previous) => ({
                      ...previous,
                      [rowKey]: merged,
                    }));
                    if (merged.length > 0) {
                      setRoleErrorsByRowId((previous) => {
                        if (!previous[rowKey]) {
                          return previous;
                        }
                        const updated = { ...previous };
                        delete updated[rowKey];
                        return updated;
                      });
                    }
                  }}
                  onClose={() => {
                    commitRolesOnClose(row);
                  }}
                  input={<OutlinedInput error={!!roleError} label="Roles" />}
                  renderValue={(selected) =>
                    formatRoleSelectionDisplay(
                      selected,
                      dropdownOptions,
                      roleLabelMap
                    )
                  }
                  disabled={!hasRoleChoices || !rolesEditable}
                >
                  {dropdownOptions.map((opt) => (
                    <MenuItem
                      key={String(opt.value)}
                      value={String(opt.value)}
                      disabled={Boolean(opt.locked)}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {roleError ? <ErrorText error>{roleError}</ErrorText> : null}
              </MultiSelectFormControl>
            </Box>
          );
        },
      },
      {
        id: "accessStatus",
        label: headers[5],
        sortable: false,
        filter: {
          type: "select",
          options: CUSTOMER_USERS_ACCESS_STATUS_FILTER_OPTIONS,
        },
        render: (_, row) => (
          <AccessStatusChip status={row.accessVariant} />
        ),
      },
      {
        id: "accountStatus",
        label: headers[6],
        sortable: false,
        render: (_, row) => (
          <AccessStatusChip status={row.accountStatus} />
        ),
      },
      {
        id: "action",
        label: headers[7],
        sortable: false,
        render: (_, row) => {
          const user = row.sourceUser;
          const rowAction = getCustomerUserListRowAction(user);
          if (!rowAction || typeof onRowAction !== "function") {
            return null;
          }
          return (
            <Box onClick={(event) => event.stopPropagation()}>
              <AccessActionButton
                type="button"
                onClick={() =>
                  onRowAction(user, rowAction.action, getRowRolesValue(row))
                }
              >
                {rowAction.label}
              </AccessActionButton>
            </Box>
          );
        },
      },
    ],
    [
      commitRolesOnClose,
      getRowRolesValue,
      headers,
      onRowAction,
      roleErrorsByRowId,
      grantableOptions,
      roleLabelMap,
    ]
  );

  const tableFilterValues = useMemo(
    () => mapCustomerUsersListFiltersToColumnFilters(listFilters),
    [listFilters]
  );

  const filtersActive = hasActiveCustomerFilters(listFilters);

  return (
    <FilterablePaginatedTable
      columns={columns}
      rows={rows}
      loading={loading}
      getRowId={(row) => row.id}
      showExport={false}
      serverMode
      page={page}
      totalRows={totalCount}
      defaultRowsPerPage={size}
      rowsPerPageOptions={PLATFORM_USER_ACCESS_LAYOUT.tableRowsPerPageOptions}
      onPageChange={(nextPage) => fetchUsers(nextPage, size)}
      onRowsPerPageChange={(nextSize) => fetchUsers(0, nextSize)}
      onFilterChange={onListFiltersChange}
      filterValues={tableFilterValues}
      applyTextFiltersOnCommit
      toolbarActions={
        <>
          <ToolbarClearFiltersButton
            variant="outlined"
            disabled={!filtersActive}
            onClick={onClearFilters}
          >
            {PLATFORM_USER_ACCESS_UI.clearFiltersLabel}
          </ToolbarClearFiltersButton>
          <ToolbarInviteButton variant="outlined" onClick={onInviteUser}>
            {PLATFORM_USER_ACCESS_UI.inviteUserLabel}
          </ToolbarInviteButton>
        </>
      }
    />
  );
};

export default UsersList;

UsersList.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  users: PropTypes.array,
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    size: PropTypes.number,
    totalCount: PropTypes.number,
    totalPages: PropTypes.number,
    hasNext: PropTypes.bool,
    hasPrevious: PropTypes.bool,
  }),
  listFilters: PropTypes.object,
  onInviteUser: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func,
  onListFiltersChange: PropTypes.func.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object),
  roleLabelMap: PropTypes.object,
  onAssignRoles: PropTypes.func.isRequired,
  onPatchCustomerAccess: PropTypes.func.isRequired,
  onRowAction: PropTypes.func.isRequired,
};
